// Copyright Caproc Oy
// System name: Aqua-Net
// System module: Company
// Author: TS
//
// 4.6.2026 TS (claude) Initial version - cross-company device list with active/archived tabs and company filter
// 4.6.2026 TS (claude) Added CSV export of the currently visible devices (respects company filter + active/archive tab)

import React from "react";
import { Message, Header, Table, Segment, Dimmer, Loader, Image, Dropdown, Menu, Button } from "semantic-ui-react";
import Shell from "./Shell";
import { authAxios, exportToCsv } from "../../utils";
import { setActivity } from "../../store/actions/auth";
import { withRouter, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from 'react-i18next';
import { companyDevicesURL } from "../../constants";


class CompanyDevices extends React.Component {
    state = {
        devices: null,
        companies: null,
        selectedCompany: "",
        activeMenuItem: this.props.t("Active"),
        error: null,
        loading: false
    };

    componentDidMount() {
        this.props.setActivity();
        this.fetchDevices();
    }

    fetchDevices = () => {
        this.setState({ loading: true });
        const companyId = this.state.selectedCompany || null;
        authAxios
            .get(companyDevicesURL(companyId))
            .then(res => {
                this.setState({
                    devices: res.data.devices,
                    companies: res.data.companies,
                    error: null,
                    loading: false
                });
            })
            .catch(err => {
                this.setState({
                    loading: false,
                    error: err
                });
            });
    };

    handleCompanyChange = (e, { value }) => {
        this.setState({ selectedCompany: value }, () => {
            this.fetchDevices();
        });
    };

    handleMenuItemClick = (e, { name }) => this.setState({ activeMenuItem: name });

    // Devices currently visible: respects the company filter (already applied
    // server-side via fetchDevices) and the active/archive tab.
    getVisibleDevices = () => {
        const { devices, activeMenuItem } = this.state;
        const { t } = this.props;
        if (!devices) {
            return [];
        }
        return devices.filter(d =>
            (activeMenuItem === t("Active") && d.is_active) ||
            (activeMenuItem === t("Archive") && !d.is_active)
        );
    };

    handleExportToCSV = () => {
        const { t, activeCompanyId } = this.props;
        const items = this.getVisibleDevices();
        const titles = [
            t("Company"),
            t("Device name"),
            t("Address"),
            t("EUI"),
            t("Model"),
            t("Local name"),
            t("Last status"),
            t("Status")
        ];
        // Key order must match the titles order — generate_csv_response writes
        // values in object insertion order.
        const items_to_csv = items.map(d => ({
            company_name: d.company_name,
            device_name: d.device_name,
            device_address: d.device_address,
            device_eui: d.device_eui,
            model_name: d.model_name,
            local_name: d.local_name,
            last_status: this.formatDateTime(d.last_status),
            last_display_value: d.last_display_value
        }));
        exportToCsv(activeCompanyId, "devices", titles, items_to_csv);
    };

    formatDateTime = (value) => {
        if (!value) {
            return "";
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString("fi-FI");
    };

    render() {
        const { error, loading, devices, companies, selectedCompany, activeMenuItem } = this.state;
        const { isAuthenticated, t } = this.props;
        if (!isAuthenticated) {
            return <Redirect to="/login" />;
        }

        const companyOptions = companies ? [
            { key: 'all', value: '', text: t("All companies") },
            ...companies.map(c => ({ key: c.id, value: c.id, text: c.name }))
        ] : [];

        return (
            <Shell>
                <Header as="h3">{t("Devices")}</Header>

                <Button
                    icon="download"
                    content="CSV"
                    floated="right"
                    color="green"
                    disabled={this.getVisibleDevices().length === 0}
                    onClick={this.handleExportToCSV}
                />

                {companies && (
                    <Dropdown
                        placeholder={t("Select company")}
                        selection
                        options={companyOptions}
                        value={selectedCompany}
                        onChange={this.handleCompanyChange}
                        style={{ marginBottom: "1em" }}
                    />
                )}

                {error && (
                    <Message
                        error
                        header={t("There was an error")}
                        content={JSON.stringify(error)}
                    />
                )}
                {loading && (
                    <Segment>
                        <Dimmer active inverted>
                            <Loader inverted>Loading</Loader>
                        </Dimmer>
                        <Image src="https://react.semantic-ui.com/images/wireframe/short-paragraph.png" />
                    </Segment>
                )}

                <Menu attached="top" tabular>
                    <Menu.Item
                        name={t("Active")}
                        active={activeMenuItem === t("Active")}
                        onClick={this.handleMenuItemClick} />
                    <Menu.Item
                        name={t("Archive")}
                        active={activeMenuItem === t("Archive")}
                        onClick={this.handleMenuItemClick} />
                </Menu>

                {devices && (
                    <Table striped celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t("Company")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Device name")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Address")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("EUI")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Model")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Local name")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Last status")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Status")}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {devices.map((device) => {
                                if ((activeMenuItem === t("Active") && device.is_active) || (activeMenuItem === t("Archive") && !device.is_active)) {
                                    return (
                                        <Table.Row key={device.id}>
                                            <Table.Cell>{device.company_name}</Table.Cell>
                                            <Table.Cell>{device.device_name}</Table.Cell>
                                            <Table.Cell>{device.device_address}</Table.Cell>
                                            <Table.Cell>{device.device_eui}</Table.Cell>
                                            <Table.Cell>{device.model_name}</Table.Cell>
                                            <Table.Cell>{device.local_name}</Table.Cell>
                                            <Table.Cell>{this.formatDateTime(device.last_status)}</Table.Cell>
                                            <Table.Cell>{device.last_display_value}</Table.Cell>
                                        </Table.Row>
                                    );
                                }
                                return null;
                            })}
                        </Table.Body>
                    </Table>
                )}
            </Shell>
        );
    }
}

const mapStateToProps = state => {
    return {
        activeCompanyId: state.company.activeCompanyId,
        isAuthenticated: state.auth.token !== null
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setActivity: () => dispatch(setActivity())
    };
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(withTranslation('companydevices')(CompanyDevices))
);
